import {
  Button,
  InlineAlert,
  Page,
  PageActions,
  PageContent,
  PageDescription,
  PageHeader,
  PageTitle,
  PageTitleBlock,
  TableSkeleton,
} from '@planuze/ui';
import { RefreshCw } from 'lucide-react';
import { DownloadsByVersionTable } from '../components/metrics/downloads-by-version-table.js';
import { DownloadsCharts, DownloadsKpis } from '../components/metrics/downloads-overview.js';
import { useDownloadsContent } from '../content/sections/downloads.js';
import { useDownloadsByVersion } from '../hooks/use-metrics.js';
import { fmtDateTime } from '../lib/format.js';

export function DownloadsPage() {
  const copy = useDownloadsContent();
  const query = useDownloadsByVersion();

  return (
    <Page>
      <PageHeader>
        <PageTitleBlock>
          <PageTitle>{copy.pageTitle}</PageTitle>
          <PageDescription>{copy.pageDescription}</PageDescription>
        </PageTitleBlock>
        <PageActions>
          <Button
            variant="outline"
            size="sm"
            disabled={query.isFetching}
            onClick={() => query.refetch().catch(() => undefined)}
          >
            <RefreshCw
              className={query.isFetching ? 'size-4 animate-spin' : 'size-4'}
              aria-hidden="true"
            />
            {copy.refresh}
          </Button>
        </PageActions>
      </PageHeader>
      <PageContent className="space-y-6">
        <InlineAlert>{copy.semantics}</InlineAlert>
        {query.data !== undefined && (
          <p className="text-sm text-muted-foreground">
            {copy.updatedAt} {fmtDateTime(query.data.asOf)} ·{' '}
            {copy.windowLabel(query.data.windowHours)}
          </p>
        )}
        {query.data?.activityAvailable === false && (
          <InlineAlert>{copy.activityUnavailable}</InlineAlert>
        )}
        {query.isError ? (
          <InlineAlert variant="destructive">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span>{copy.loadError}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => query.refetch().catch(() => undefined)}
              >
                {copy.retry}
              </Button>
            </div>
          </InlineAlert>
        ) : (
          <>
            <DownloadsKpis data={query.data} loading={query.isLoading} />
            <DownloadsCharts data={query.data} loading={query.isLoading} />
            <section className="space-y-3" aria-labelledby="downloads-details-title">
              <h2 id="downloads-details-title" className="font-semibold text-lg text-foreground">
                {copy.detailsTitle}
              </h2>
              {query.isLoading ? (
                <TableSkeleton columns={6} />
              ) : (
                <DownloadsByVersionTable items={query.data?.items ?? []} />
              )}
            </section>
          </>
        )}
      </PageContent>
    </Page>
  );
}
